import type { EntitySummary, EventSummary } from '@/lib/types';
import { Save } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { FloatingSaveButton } from '@/components/floating-save-button';

import AuthGuard from '@/components/auth-guard';
import { Breadcrumb } from '@/components/breadcrumb';
import { EventDetailsDialog } from '@/components/event-details-dialog';
import { MultiSelectComboBox } from '@/components/multi-select-combobox';
import { PageHeader } from '@/components/page-header';
import { SceneMetaFields } from '@/features/scenes/components/SceneMetaFields';
import { useCreateSceneForm } from '@/features/scenes/hooks/useCreateSceneForm';
const sceneSearchSchema = z.object({
  chapterId: z.string().optional(),
});
export const Route = createFileRoute('/scenes/new')({
  validateSearch: (search) => sceneSearchSchema.parse(search),
  component: NewScenePage,
});
function NewSceneForm() {
  const {
    register,
    control,
    handleSubmit,
    errors,
    isSaving,
    characters,
    locations,
    organizations,
    events,
    isLoadingCharacters,
    isLoadingLocations,
    isLoadingOrganizations,
    isLoadingEvents,
    selectedEvents,
    selectedCharacterNames,
    selectedOrganizationNames,
    selectedEventForPopup,
    setSelectedEventForPopup,
    novelId,
    t,
  } = useCreateSceneForm();
  if (!novelId) return null;
  const entityOptions = (entities: (EntitySummary | EventSummary)[] | null | undefined) =>
    entities?.map((e) => ({ value: e.id, label: e.name })) || [];
  return (
    <AuthGuard>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 0 }}>
        <Breadcrumb
          path={[
            { label: t('entity_list.scenes.title'), href: '/scenes' },
            { label: t('entity_form.scenes.new_scene_title') }
          ]}
        />
        <PageHeader
          title={t('entity_form.scenes.new_scene_title')}
          description={t('entity_form.scenes.new_scene_desc')}
          actions={
            <Button
              type="submit"
              form="scene-form"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
            >
              {isSaving ? t('entity_form.scenes.creating') : t('entity_form.scenes.create')}
            </Button>
          }
        />
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardHeader
            title={t('entity_form.scenes.scene_details')}
            titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
          />
          <CardContent>
            <form id="scene-form" onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      {...register('name')}
                      label={t('entity_form.scenes.name_label')}
                      placeholder={t('entity_form.scenes.name_placeholder')}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Box>
                      <TextField
                        {...register('description')}
                        label={t('entity_form.scenes.description_label')}
                        placeholder={t('entity_form.scenes.description_placeholder')}
                        fullWidth
                        multiline
                        rows={6}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        {selectedEvents.map(event => (
                          <Chip
                            key={event.id}
                            label={event.name}
                            onClick={() => setSelectedEventForPopup(event)}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="eventIds"
                      control={control}
                      render={({ field }) => (
                        <MultiSelectComboBox
                          options={entityOptions(events)}
                          selected={field.value || []}
                          onChange={field.onChange}
                          placeholder={t('entity_form.scenes.select_events_placeholder')}
                          searchPlaceholder={t('entity_form.scenes.search_events')}
                          emptyPlaceholder={t('entity_form.scenes.no_events_found')}
                          isLoading={isLoadingEvents}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="povCharacterId"
                      control={control}
                      render={({ field }) => (
                        <MultiSelectComboBox
                          options={entityOptions(characters)}
                          selected={field.value ? [field.value] : []}
                          onChange={(ids) => {
                            const prevValue = field.value;
                            const newValue = ids.find(id => id !== prevValue) || ids[0] || '';
                            field.onChange(newValue);
                          }}
                          placeholder={t('entity_form.scenes.select_pov_placeholder')}
                          searchPlaceholder={t('entity_form.scenes.search_characters')}
                          emptyPlaceholder={t('entity_form.scenes.no_characters_found')}
                          isLoading={isLoadingCharacters}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Box>
                      <TextField
                        {...register('minorCharacters')}
                        label={t('entity_form.scenes.minor_characters_label')}
                        placeholder={t('entity_form.scenes.minor_characters_placeholder')}
                        fullWidth
                        multiline
                        rows={3}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        {selectedCharacterNames.map((name: string) => (
                          <Chip key={name} label={name} size="small" variant="outlined" />
                        ))}
                        {selectedOrganizationNames.map((name: string) => (
                          <Chip key={name} label={name} size="small" variant="outlined" color="info" />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box id="scene-characters-selector-step">
                      <Controller
                        name="characterIds"
                        control={control}
                        render={({ field }) => (
                          <MultiSelectComboBox
                            options={entityOptions(characters)}
                            selected={field.value || []}
                            onChange={field.onChange}
                            placeholder={t('entity_form.scenes.select_characters_placeholder')}
                            searchPlaceholder={t('entity_form.scenes.search_characters')}
                            emptyPlaceholder={t('entity_form.scenes.no_characters_found')}
                            isLoading={isLoadingCharacters}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Controller
                      name="organizationIds"
                      control={control}
                      render={({ field }) => (
                        <MultiSelectComboBox
                          options={entityOptions(organizations)}
                          selected={field.value || []}
                          onChange={field.onChange}
                          placeholder={t('entity_form.scenes.select_organizations_placeholder')}
                          searchPlaceholder={t('entity_form.scenes.search_organizations')}
                          emptyPlaceholder={t('entity_form.scenes.no_organizations_found')}
                          isLoading={isLoadingOrganizations}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box id="scene-locations-selector-step">
                      <Controller
                        name="locationIds"
                        control={control}
                        render={({ field }) => (
                          <MultiSelectComboBox
                            options={entityOptions(locations)}
                            selected={field.value || []}
                            onChange={field.onChange}
                            placeholder={t('entity_form.scenes.select_locations_placeholder')}
                            searchPlaceholder={t('entity_form.scenes.search_locations')}
                            emptyPlaceholder={t('entity_form.scenes.no_locations_found')}
                            isLoading={isLoadingLocations}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <SceneMetaFields register={register} />
              </Stack>
            </form>
          </CardContent>
        </Card>
        <Box sx={{ height: 64 }} />
        <FloatingSaveButton
          onClick={handleSubmit}
          isSaving={isSaving}
          disabled={isSaving}
          label={t('entity_form.scenes.create')}
        />
        <EventDetailsDialog
          novelId={novelId || ''}
          event={selectedEventForPopup}
          open={!!selectedEventForPopup}
          onOpenChange={(open) => !open && setSelectedEventForPopup(null)}
          locations={locations}
          characters={characters}
          organizations={organizations}
        />
      </Container>
    </AuthGuard>
  );
}
export default function NewScenePage() {
  return (
    <Suspense fallback={<Box sx={{ p: 4 }}><CircularProgress /></Box>}>
      <NewSceneForm />
    </Suspense>
  );
}
